import { GlobalService } from 'src/app/_services/global.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { Component, Input, OnInit } from '@angular/core';
import { ProductsService } from 'src/app/_services/products.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';

@Component({
  selector: 'app-product-pricing-v2',
  templateUrl: './product-pricing-v2.component.html',
  styleUrls: ['./product-pricing-v2.component.scss']
})
export class ProductPricingV2Component implements OnInit {
  editform: FormGroup
  _globals = GlobalService

  selectedDefaultPrice:any=''

  /** new-product-v4 hides the Tax Rate dropdown column entirely. */
  @Input() hideTaxRate = false;

  /** Mirrors the business's "Collect tax" toggle (tax-setting-v2) — the "Product is Taxable"
   * checkbox only makes sense when the business collects tax at all. */
  collectTaxEnabled = false;

  constructor(
    public _productService: ProductsService,
    public _abd: AppBaseDataService,
    private _fb: FormBuilder,
    private _businessSetupService: BusinessSetupService,
  ) {}

  ngOnInit(): void {
    this.selectedDefaultPrice = this._productService.selectedDefaultPrice
    this.initForm()
    this.loadCollectTaxSetting()
    if (!this._productService.activeItem) {
      const original = this._productService.productInfo?.taxRate?.[0];

      if (original) {
        const s = { ...original };
        s.name = s.name.replace(/\s\d+$/, '');

        const percentage = this._abd.systemConfigs?.taxConfig?.tax1;
        if (percentage !== undefined && percentage !== null) {
          s.name = `${s.name} (${percentage}%)`;
        }

        this._productService.taxRates = [s];
        this._productService.selectedTaxRate = s;
      }
    }
  }

  /** Hides "Product is Taxable" and forces it off when the business has Collect tax disabled. */
  private loadCollectTaxSetting(): void {
    this._businessSetupService.getTaxSettings().subscribe({
      next: (res: any) => {
        const rawData = res?.data ?? res?.Data ?? res;
        const settings = Array.isArray(rawData) ? rawData[0] : rawData;
        this.collectTaxEnabled = !!(settings?.collectTax ?? settings?.CollectTax);
        if (!this.collectTaxEnabled) {
          this._productService.isTaxable = false;
        }
      },
      error: () => {
        this.collectTaxEnabled = false;
        this._productService.isTaxable = false;
      },
    });
  }

  get hasVariants(): boolean {
    const attributes = this._productService?.variantAttributes;
    const rows = this._productService?.variantRows;
    return (Array.isArray(attributes) && attributes.length > 0) ||
      (Array.isArray(rows) && rows.length > 0);
  }

  initForm() {
    this.editform = this._fb.group({
      priceA: [{value:this._productService.pts.priceA,disabled: this._productService.isViewOnly}, Validators.required],
      priceB: [{value:this._productService.pts.priceB,disabled: this._productService.isViewOnly}, this._globals.validateNonZeroNumber],
      priceC: [{value:this._productService.pts.priceC,disabled: this._productService.isViewOnly}, this._globals.validateNonZeroNumber],
      costPrice: [{value:this._productService.pts.costPrice,disabled: this._productService.isViewOnly}, this._globals.validatePositive],
      discountPrice: [{value:this._productService.pts.discountPrice,disabled: this._productService.isViewOnly}, this._globals.validateNonZeroNumber],
      caseQty: [{value:this._productService.pts.caseQty,disabled: this._productService.isViewOnly}, this._globals.validateNonZeroNumber],
    })
  }

  sellQtyChanged(e) {}

  defaultPriceChange(e) {
    this._productService.selectedDefaultPrice = e.value
  }

  isFormValid() {
    return this.editform.valid
  }

  onPriceBlur(controlName: 'priceB' | 'priceC'): void {
    const control = this.editform?.get(controlName);
    if (!control) return;
    const current = control.value;
    const normalized = this.formatToTwoDecimals(
      current === '' || current === null || current === undefined ? 0 : current
    );
    control.setValue(normalized);
    if (controlName === 'priceB') {
      this._productService.pts.priceB = normalized;
    } else {
      this._productService.pts.priceC = normalized;
    }
  }

  private formatToTwoDecimals(value: string | number): number {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return 0;
    return parseFloat(num.toFixed(2));
  }

  onZeroValueFocus(controlName: 'priceB' | 'priceC', event: FocusEvent): void {
    const input = event?.target as HTMLInputElement | null;
    if (!input) return;
    const rawValue = (input.value || '').trim();
    if (!this.isZeroDisplayValue(rawValue)) return;
    const control = this.editform?.get(controlName);
    if (control) {
      control.setValue(null);
    }
    if (this._productService?.pts && controlName in this._productService.pts) {
      this._productService.pts[controlName] = null;
    }
    setTimeout(() => {
      try {
        input.setSelectionRange(0, 0);
      } catch {}
    }, 0);
  }

  private isZeroDisplayValue(value: string): boolean {
    const normalized = value.replace(/[^0-9.-]/g, '');
    if (!normalized) return false;
    const numericValue = Number(normalized);
    return !isNaN(numericValue) && numericValue === 0;
  }
}
